import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { BranchInput, SupplierInput } from "@reina/shared";
import type { Branch, Supplier } from "@reina/shared";
import {
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import {
  useBranchCounts,
  useBranches,
  useCreateBranch,
  useCreateSupplier,
  useSuppliers,
  useUpdateBranch,
  useUpdateSupplier,
} from "../../api/catalogue";
import { errorMessage, formatPct } from "../../lib/format";
import { numberOrNull, textOrNull } from "../../lib/form";

export function SuppliersPage() {
  const suppliers = useSuppliers();
  const items = suppliers.data?.items ?? [];
  const branchCounts = useBranchCounts(items.map((supplier) => supplier.id));
  const [modal, setModal] = useState<{ supplier: Supplier | null } | null>(null);

  const newButton = (
    <Button variant="primary" onClick={() => setModal({ supplier: null })}>
      New supplier
    </Button>
  );

  return (
    <>
      <Panel>
        <PanelHead
          title="Suppliers"
          sub={
            suppliers.data
              ? `${suppliers.data.total} merchant account${suppliers.data.total === 1 ? "" : "s"}`
              : "merchant accounts + branches"
          }
          actions={newButton}
        />
        {suppliers.isPending ? (
          <PanelBody>
            <EmptyState title="Loading suppliers…" />
          </PanelBody>
        ) : suppliers.isError ? (
          <PanelBody>
            <EmptyState title="Could not load suppliers" hint={errorMessage(suppliers.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No suppliers yet"
              hint="Create the first merchant account to start the catalogue."
              action={newButton}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Parent group</th>
                <th>Default discount</th>
                <th>Branches</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((supplier) => (
                <tr
                  key={supplier.id}
                  onClick={() => setModal({ supplier })}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <span className="result-cell">
                      <SupplierAvatar name={supplier.name} logo={supplier.logo} />
                      <strong>{supplier.name}</strong>
                    </span>
                  </td>
                  <td>{supplier.parentGroup ?? "—"}</td>
                  <td className="mono">{formatPct(supplier.defaultDiscountPct)}</td>
                  <td className="mono">{branchCounts[supplier.id] ?? "—"}</td>
                  <td>
                    <Pill tone={supplier.isActive ? "ok" : "neutral"}>
                      {supplier.isActive ? "Active" : "Inactive"}
                    </Pill>
                  </td>
                  <td>
                    <Button
                      mini
                      onClick={(event) => {
                        event.stopPropagation();
                        setModal({ supplier });
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {modal && (
        <SupplierModal
          key={modal.supplier?.id ?? "new"}
          supplier={modal.supplier}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Create/edit modal (branches editable once the supplier exists)
// ---------------------------------------------------------------------------

function SupplierModal({ supplier, onClose }: { supplier: Supplier | null; onClose: () => void }) {
  const toast = useToast();
  const create = useCreateSupplier();
  const update = useUpdateSupplier();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof SupplierInput>, unknown, z.output<typeof SupplierInput>>({
    resolver: zodResolver(SupplierInput),
    defaultValues: {
      name: supplier?.name ?? "",
      parentGroup: supplier?.parentGroup ?? "",
      defaultDiscountPct: supplier?.defaultDiscountPct ?? undefined,
      notes: supplier?.notes ?? "",
      logo: supplier?.logo ?? "",
      isActive: supplier?.isActive ?? true,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (supplier) {
        await update.mutateAsync({ id: supplier.id, input: values });
        toast.push({ message: "Supplier updated", tone: "ok" });
      } else {
        await create.mutateAsync(values);
        toast.push({ message: "Supplier created", tone: "ok" });
      }
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title={supplier ? "Edit supplier" : "New supplier"}
      sub={supplier ? supplier.name : "add a merchant account"}
      onClose={onClose}
      footer={
        <>
          <span className="sub">
            {supplier ? "Changes are stamped and audited." : "Branches can be added after saving."}
          </span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="supplier-form" disabled={isSubmitting}>
              {supplier ? "Save changes" : "Create supplier"}
            </Button>
          </div>
        </>
      }
    >
      <form id="supplier-form" className="stack" onSubmit={onSubmit} noValidate>
        <FieldGrid>
          <Field label="Name" error={errors.name?.message}>
            <input className="field" autoFocus {...register("name")} />
          </Field>
          <Field label="Parent group" hint="e.g. Highbourne Group" error={errors.parentGroup?.message}>
            <input className="field" {...register("parentGroup", { setValueAs: textOrNull })} />
          </Field>
          <Field label="Default discount %" error={errors.defaultDiscountPct?.message}>
            <input
              className="field"
              type="number"
              step="0.01"
              min={0}
              max={100}
              {...register("defaultDiscountPct", { setValueAs: numberOrNull })}
            />
          </Field>
          <Field
            label="Logo path"
            hint="e.g. /logos/graham.png — initials shown when empty"
            error={errors.logo?.message}
          >
            <input className="field" {...register("logo", { setValueAs: textOrNull })} />
          </Field>
        </FieldGrid>
        <Field label="Notes" error={errors.notes?.message}>
          <textarea className="field" rows={3} {...register("notes", { setValueAs: textOrNull })} />
        </Field>
        <Field label="Active" hint="Untick to soft-delete — hidden from pickers, history kept.">
          <input type="checkbox" {...register("isActive")} />
        </Field>
      </form>
      {supplier && <BranchesSection supplierId={supplier.id} />}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Branches (own <form> elements — never nested inside the supplier form)
// ---------------------------------------------------------------------------

function BranchesSection({ supplierId }: { supplierId: string }) {
  const branches = useBranches(supplierId);
  const [editing, setEditing] = useState<{ branch: Branch | null } | null>(null);
  const items = branches.data?.items ?? [];

  return (
    <section className="stack">
      <div className="actions" style={{ justifyContent: "space-between" }}>
        <span className="label">Branches</span>
        <Button mini onClick={() => setEditing({ branch: null })}>
          Add branch
        </Button>
      </div>
      {branches.isPending ? (
        <EmptyState title="Loading branches…" />
      ) : branches.isError ? (
        <EmptyState title="Could not load branches" hint={errorMessage(branches.error)} />
      ) : items.length === 0 && !editing ? (
        <EmptyState
          title="No branches"
          hint="e.g. Graham — Dover, Plumbase — Canterbury, Graham — Folkestone"
        />
      ) : (
        <div className="line-list">
          {items.map((branch) => (
            <div key={branch.id} className="line-row">
              <span>
                <strong>{branch.name}</strong>
                <span className="sub">
                  {[branch.branchBrand, branch.town].filter(Boolean).join(" — ") || "—"}
                </span>
              </span>
              <Button mini onClick={() => setEditing({ branch })}>
                Edit
              </Button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <BranchForm
          key={editing.branch?.id ?? "new"}
          supplierId={supplierId}
          branch={editing.branch}
          onDone={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function BranchForm({
  supplierId,
  branch,
  onDone,
}: {
  supplierId: string;
  branch: Branch | null;
  onDone: () => void;
}) {
  const toast = useToast();
  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof BranchInput>, unknown, z.output<typeof BranchInput>>({
    resolver: zodResolver(BranchInput),
    defaultValues: {
      name: branch?.name ?? "",
      branchBrand: branch?.branchBrand ?? "",
      town: branch?.town ?? "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (branch) {
        await updateBranch.mutateAsync({ supplierId, branchId: branch.id, input: values });
        toast.push({ message: "Branch updated", tone: "ok" });
      } else {
        await createBranch.mutateAsync({ supplierId, input: values });
        toast.push({ message: "Branch added", tone: "ok" });
      }
      onDone();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <form className="stack" onSubmit={onSubmit} noValidate>
      <FieldGrid columns={3}>
        <Field label="Branch name" error={errors.name?.message}>
          <input className="field" autoFocus {...register("name")} />
        </Field>
        <Field
          label="Trading brand"
          hint="e.g. Graham / Plumbase"
          error={errors.branchBrand?.message}
        >
          <input className="field" {...register("branchBrand", { setValueAs: textOrNull })} />
        </Field>
        <Field label="Town" error={errors.town?.message}>
          <input className="field" {...register("town", { setValueAs: textOrNull })} />
        </Field>
      </FieldGrid>
      <div className="actions">
        <Button mini onClick={onDone}>
          Cancel
        </Button>
        <Button mini variant="primary" type="submit" disabled={isSubmitting}>
          {branch ? "Save branch" : "Add branch"}
        </Button>
      </div>
    </form>
  );
}
